<!-- ── Trigger / readonly display ─────────────────────────────────────── -->
@if (isReadonly()) {
  @if (selectedReadonlyClient(); as sc) {
    <div class="msv2-selected-card" data-testid="mini-search-v2-selected-readonly" aria-label="mini-search-v2-readonly-client">
      <div class="flex align-items-center gap-2 min-w-0">
        @if (sc.clt_image) {
          <p-avatar image="{{ sc.clt_image }}" shape="circle" size="normal" aria-hidden="true" />
        } @else {
          <p-avatar [label]="client2LetterName(sc.clt_full_name) | titlecase | shortName" shape="circle" size="normal" aria-hidden="true" />
        }
        <span class="font-semibold text-sm text-primary white-space-nowrap overflow-hidden text-overflow-ellipsis">{{ sc.clt_full_name }}</span>
        <span class="text-xs text-500">· {{ sc.clt_id }}</span>
      </div>
      <div [innerHTML]="clientStatus(sc)"></div>
    </div>
  }
} @else {
  <!-- Inline selected strip (appointment: show selected client above the trigger) -->
  @if (showSelected() && selectedReadonlyClient(); as sc) {
    <div class="msv2-selected-card mb-2" data-testid="mini-search-v2-selected-inline">
      <div class="flex align-items-center gap-2 min-w-0 flex-1">
        @if (sc.clt_image) {
          <p-avatar image="{{ sc.clt_image }}" shape="circle" size="normal" aria-hidden="true" />
        } @else {
          <p-avatar [label]="client2LetterName(sc.clt_full_name) | titlecase | shortName" shape="circle" size="normal" aria-hidden="true" />
        }
        <span class="font-semibold text-sm text-primary white-space-nowrap overflow-hidden text-overflow-ellipsis">{{ sc.clt_full_name }}</span>
        <span class="text-xs text-500">· {{ sc.clt_id }}</span>
      </div>
      @if (!isDisabled()) {
        <button type="button" class="msv2-icon-btn" (click)="clearReadonlyClient(); $event.stopPropagation()" aria-label="mini-search-v2-clear-selection">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      }
    </div>
  }

  <!-- Search trigger bar (hide when client is selected and showSelected is true) -->
  @if (!showSelected() || !selectedReadonlyClient()) {
    <div
      class="msv2-trigger"
      [class.msv2-trigger--disabled]="isDisabled()"
      (click)="openDialog()"
      (keydown.enter)="openDialog()"
      (keydown.space)="$event.preventDefault(); openDialog()"
      tabindex="0"
      role="button"
      aria-label="open-mini-search-v2"
      data-testid="mini-search-v2-open-trigger"
    >
      <i class="fa fa-search msv2-trigger__icon" aria-hidden="true"></i>
      <span class="msv2-trigger__text">{{ 'Shared.SEARCH.MINI-SEARCH.PLACEHOLDER-SEARCH-CLIENT' | translate }}</span>
    </div>
  }
}

<!-- ── Dialog ──────────────────────────────────────────────────────────── -->
<p-dialog
  [visible]="dialogOpen()"
  (visibleChange)="onDialogVisibleChange($event)"
  [modal]="true"
  [dismissableMask]="true"
  [closable]="false"
  appendTo="body"
  [draggable]="false"
  [resizable]="false"
  [breakpoints]="{ '768px': '100vw' }"
  [style]="{ width: '600px', maxWidth: '100vw', padding: '0' }"
  styleClass="msv2-dialog"
  aria-label="mini-search-v2-dialog"
  data-testid="mini-search-v2-dialog"
>
  <ng-template pTemplate="header">
    <!-- Header: title + subtitle + close -->
    <div class="msv2-header">
      <div class="min-w-0 flex-1">
        <h2 class="msv2-header__title" data-testid="mini-search-v2-title">
          {{ dialogTitle() || ('Shared.SEARCH.MINI-SEARCH.PLACEHOLDER-SEARCH-CLIENT' | translate) }}
        </h2>
        @if (dialogSubtitle()) {
          <p class="msv2-header__subtitle">{{ dialogSubtitle() }}</p>
        }
      </div>
      <button type="button" class="msv2-icon-btn msv2-icon-btn--close" (click)="closeDialog()" aria-label="mini-search-v2-close" data-testid="mini-search-v2-close">
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
    </div>
  </ng-template>

  <ng-template pTemplate="content">
    <div class="msv2-body">

      <!-- ── Search input ──────────────────────────────────────────────── -->
      <div class="msv2-search-wrap">
        <i class="fa fa-search msv2-search-wrap__icon" aria-hidden="true"></i>
        <input
          type="text"
          class="msv2-search-wrap__input"
          [ngModel]="searchInput()"
          (ngModelChange)="onSearchFieldInput($event)"
          [placeholder]="'Shared.SEARCH.MINI-SEARCH.PLACEHOLDER-SEARCH-CLIENT' | translate"
          aria-label="mini-search-v2-query"
          data-testid="mini-search-v2-query"
          autocomplete="off"
        />
        @if (searchInput()) {
          <button type="button" class="msv2-search-wrap__clear" (click)="clearSearchInput()" aria-label="mini-search-v2-clear-query">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        }
      </div>

      @if (currentQuery().length >= 1) {
        <!-- ── Centre scope toggle ─────────────────────────────────────── -->
        <div class="msv2-scope-toggle">
          <button
            type="button"
            class="msv2-scope-btn"
            [class.msv2-scope-btn--active]="!allCentresMode()"
            (click)="setCentreMode(false)"
            aria-label="mini-search-v2-this-centre"
            [attr.aria-pressed]="!allCentresMode()"
          >
            <i class="fa fa-map-marker-alt msv2-scope-btn__icon" aria-hidden="true"></i>
            <span class="msv2-scope-btn__label">{{ thisCentreButtonLabel() }}</span>
            <span class="msv2-badge" [class.msv2-badge--active]="!allCentresMode()">{{ thisCentreCountBadge() }}</span>
          </button>
          <button
            type="button"
            class="msv2-scope-btn msv2-scope-btn--right"
            [class.msv2-scope-btn--active]="allCentresMode()"
            (click)="setCentreMode(true)"
            aria-label="mini-search-v2-all-centres"
            [attr.aria-pressed]="allCentresMode()"
          >
            <i class="fa fa-building msv2-scope-btn__icon" aria-hidden="true"></i>
            <span class="msv2-scope-btn__label">{{ 'Shared.SEARCH.MINI-SEARCH.ALL_CENTRES' | translate }}</span>
            <span class="msv2-badge" [class.msv2-badge--active]="allCentresMode()">{{ allCentreCountBadge() }}</span>
          </button>
        </div>

        <!-- ── Status filter chips (local mode only) ───────────────────── -->
        @if (!allCentresMode() && segments().length) {
          <div class="msv2-filter-row" role="group" aria-label="filter by status">
            <button
              type="button"
              class="msv2-filter-chip"
              [class.msv2-filter-chip--active-all]="activeFilter() === 'all'"
              (click)="setActiveFilter('all')"
              aria-label="mini-search-v2-filter-all"
              [attr.aria-pressed]="activeFilter() === 'all'"
            >
              {{ 'Shared.SEARCH.MINI-SEARCH.FILTER_ALL_STATUS' | translate }}
              <span class="msv2-filter-chip__count">{{ thisCentreCountBadge() }}</span>
            </button>
            @for (s of segments(); track s.label) {
              @if (s.total > 0) {
                <button
                  type="button"
                  class="msv2-filter-chip"
                  [class.msv2-filter-chip--active]="activeFilter() === s.label"
                  [attr.data-status]="s.label | lowercase"
                  (click)="setActiveFilter(s.label)"
                  [attr.aria-label]="'mini-search-v2-filter-' + s.label"
                  [attr.aria-pressed]="activeFilter() === s.label"
                >
                  <span class="msv2-filter-chip__dot" [attr.data-status]="s.label | lowercase"></span>
                  {{ statLabel(s.label) }}
                  <span class="msv2-filter-chip__count">{{ s.total }}</span>
                </button>
              }
            }
          </div>
        }
      }

      <!-- ── Results area ────────────────────────────────────────────── -->
      <div class="msv2-results">
        @if (loading()) {
          <div class="msv2-empty" aria-label="mini-search-v2-loading">
            <p-progressSpinner styleClass="w-3rem h-3rem" strokeWidth="3" />
          </div>
        } @else if (currentQuery().length < 3) {
          <div class="msv2-empty" aria-label="mini-search-v2-empty-prompt">
            <div class="msv2-empty__icon-wrap"><i class="fa fa-search" aria-hidden="true"></i></div>
            <p class="msv2-empty__title">{{ 'Shared.SEARCH.MINI-SEARCH.EMPTY_PROMPT_TITLE' | translate }}</p>
            <p class="msv2-empty__sub">{{ 'Shared.SEARCH.MINI-SEARCH.EMPTY_PROMPT_BODY' | translate }}</p>
          </div>
        } @else if (allCentresMode()) {
          <!-- All centres: flat list -->
          @if (companyClients().length === 0) {
            <div class="msv2-empty">
              <div class="msv2-empty__icon-wrap"><i class="fa fa-users" aria-hidden="true"></i></div>
              <p class="msv2-empty__title">{{ 'Shared.SEARCH.MINI-SEARCH.ALL_CENTRES_EMPTY_TITLE' | translate }}</p>
              <p class="msv2-empty__sub">{{ 'Shared.SEARCH.MINI-SEARCH.ALL_CENTRES_EMPTY_BODY' | translate }}</p>
            </div>
          } @else {
            <div class="msv2-results-meta">
              {{ 'Shared.SEARCH.MINI-SEARCH.ALL_CENTRES_RESULTS_LEAD' | translate }}
              <strong>{{ 'Shared.SEARCH.MINI-SEARCH.ALL_CENTRES_RESULTS_FOUND' | translate: { total: allCentresTotal() } }}</strong>
            </div>
            @for (client of companyClients(); track client.clt_id) {
              <div class="msv2-row msv2-row--all" (click)="pickClient(client)" tabindex="0" role="button"
                   (keydown.enter)="pickClient(client)" [attr.aria-label]="'Select ' + client.clt_full_name">
                <div class="msv2-row__avatar">
                  @if (client.clt_image) {
                    <p-avatar image="{{ client.clt_image }}" shape="circle" size="normal" aria-hidden="true" />
                  } @else {
                    <div class="msv2-initials" [attr.data-initials]="client2LetterName(client.clt_full_name) | titlecase | shortName">
                      {{ client2LetterName(client.clt_full_name) | titlecase | shortName }}
                    </div>
                  }
                </div>
                <div class="msv2-row__info flex-1 min-w-0">
                  <div class="flex align-items-center gap-2 flex-wrap">
                    <span class="msv2-row__name" [innerHTML]="client.clt_full_name | highlight: searchInput()"></span>
                    <span [innerHTML]="clientStatus(client)" class="msv2-status-badge-wrap"></span>
                  </div>
                  <div class="msv2-row__meta">
                    <span>ID {{ client.clt_id }}</span>
                    @if (getTenant(client); as tenant) {
                      <span><i class="fa fa-building" aria-hidden="true"></i> {{ tenant.name_primary_language }}</span>
                    }
                    @if (client.clt_mobile_no) {
                      <span><i class="fa fa-phone" aria-hidden="true"></i> {{ client.clt_mobile_country_code }} {{ client.clt_mobile_no }}</span>
                    }
                  </div>
                </div>
                <button type="button" class="msv2-add-btn" (click)="pickClient(client); $event.stopPropagation()"
                        [attr.aria-label]="'mini-search-v2-add-member-' + client.clt_id">
                  <i class="fa fa-user-plus" aria-hidden="true"></i>
                  {{ 'common.BUTTONS.BTN_ADD' | translate }}
                </button>
              </div>
            }
            @if (companyHasMore()) {
              <div class="msv2-load-more">
                <button type="button" class="msv2-text-btn" (click)="loadMoreCompany()" aria-label="mini-search-v2-load-more">
                  <i class="fa fa-refresh" aria-hidden="true"></i>
                  {{ 'common.BUTTONS.BTN_LOAD_MORE' | translate }}
                </button>
              </div>
            }
          }
        } @else {
          <!-- This centre: grouped by status sections -->
          @if (!hasLocalResults()) {
            <div class="msv2-empty">
              <div class="msv2-empty__icon-wrap"><i class="fa fa-users" aria-hidden="true"></i></div>
              <p class="msv2-empty__title">{{ 'Shared.SEARCH.MINI-SEARCH.LOCAL_EMPTY_TITLE' | translate }}</p>
              <p class="msv2-empty__sub">{{ 'Shared.SEARCH.MINI-SEARCH.LOCAL_EMPTY_BODY' | translate }}</p>
              <button type="button" class="msv2-text-btn mt-2" (click)="setCentreMode(true)" aria-label="mini-search-v2-switch-all-centres">
                {{ 'Shared.SEARCH.MINI-SEARCH.CTA_SEARCH_ALL_CENTRES' | translate }}
              </button>
            </div>
          } @else {
            @for (seg of visibleSegments(); track seg.label) {
              @if (seg.total > 0) {
                <!-- Section header -->
                <div class="msv2-section-header">
                  <span class="msv2-section-header__dot" [attr.data-status]="seg.label | lowercase"></span>
                  <span class="msv2-section-header__label" [attr.data-status]="seg.label | lowercase">{{ statLabel(seg.label) }}</span>
                  <span class="msv2-section-header__count">{{ 'Shared.SEARCH.MINI-SEARCH.SECTION_ITEMS_SHOWN' | translate: { count: seg.items.length } }}</span>
                  @if (!seg.fullyLoaded && seg.items.length < seg.total) {
                    <button type="button" class="msv2-show-all-btn" (click)="expandSegment(seg)"
                            [attr.aria-label]="'mini-search-v2-show-all-' + seg.label">
                      {{ 'Shared.SEARCH.MINI-SEARCH.SHOW_ALL_N_RESULTS' | translate: { n: seg.total } }}
                      <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </button>
                  }
                </div>
                <!-- Client rows -->
                @for (client of seg.items; track client.clt_id) {
                  <div class="msv2-row" (click)="pickClient(client)" tabindex="0" role="button"
                       (keydown.enter)="pickClient(client)" [attr.aria-label]="'Select ' + client.clt_full_name">
                    <div class="msv2-row__avatar">
                      @if (client.clt_image) {
                        <p-avatar image="{{ client.clt_image }}" shape="circle" size="normal" aria-hidden="true" />
                      } @else {
                        <div class="msv2-initials">
                          {{ client2LetterName(client.clt_full_name) | titlecase | shortName }}
                        </div>
                      }
                    </div>
                    <div class="msv2-row__info flex-1 min-w-0">
                      <div class="flex align-items-center gap-2 flex-wrap">
                        <span class="msv2-row__name" [innerHTML]="client.clt_full_name | highlight: searchInput()"></span>
                        <span [innerHTML]="clientStatus(client)" class="msv2-status-badge-wrap"></span>
                      </div>
                      <div class="msv2-row__meta">
                        <span>ID {{ client.clt_id }}</span>
                        @if (getTenant(client); as tenant) {
                          <span><i class="fa fa-building" aria-hidden="true"></i> {{ tenant.name_primary_language }}</span>
                        }
                        @if (client.clt_mobile_no) {
                          <span><i class="fa fa-phone" aria-hidden="true"></i> {{ client.clt_mobile_country_code }} {{ client.clt_mobile_no }}</span>
                        }
                      </div>
                    </div>
                    <button type="button" class="msv2-add-btn" (click)="pickClient(client); $event.stopPropagation()"
                            [attr.aria-label]="'mini-search-v2-add-member-' + client.clt_id">
                      <i class="fa fa-user-plus" aria-hidden="true"></i>
                      {{ 'common.BUTTONS.BTN_ADD' | translate }}
                    </button>
                  </div>
                }
              }
            }
          }
        }
      </div>

      <!-- ── Footer: create new client ───────────────────────────────── -->
      @if (showCreateClient()) {
        <div class="msv2-footer">
          <button type="button" class="msv2-text-btn" (click)="emitCreateProspect()" aria-label="mini-search-v2-create-new-client">
            <i class="fa fa-user-plus" aria-hidden="true"></i>
            {{ 'Shared.SEARCH.MINI-SEARCH.CREATENEWCLIENT' | translate }}
          </button>
        </div>
      }

    </div>
  </ng-template>
</p-dialog>
